import { canManageUsers } from './userPermissions';
import { canManageAllClaims } from './financialPermissions';
import { canManageGallery } from './galleryPermissions';
import { GALLERY_PUBLICITY_FILTER_OPTIONS } from '../constants/galleryPublicity';
import { canManageUserPrivileges } from './userPermissions';
import { canManageMaintenanceWorkflow } from './maintenancePermissions';
import { canCreatePenalty } from './eventPenaltyPermissions';

const isAdminOrTL = (user) => Boolean(user?.is_admin) || Number(user?.management_id) === 1;
const isInventoryManager = (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 6;
const isMultimedia = (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 3;
const isGameManager = (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 2;
const isPartnership = (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 1;
const isBudgetManager = (user) => isAdminOrTL(user) || Number(user?.management_id) === 3;
const isDocumentManager = (user) => isAdminOrTL(user) || Number(user?.management_id) === 4;

const UNSET = '__unset__';
export { UNSET };

const claimStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const maintenanceStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const maintenanceTypeOptions = [
  { value: 'repair', label: 'Repair' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'other', label: 'Other' },
];

const maintenancePriorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const visibilityOptions = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
];

const analysisTypeOptions = [
  { value: 'training', label: 'Training' },
  { value: 'domestic', label: 'Domestic' },
  { value: 'international', label: 'International' },
];

const analysisCountryOptions = [
  'Malaysia', 'Indonesia', 'Thailand', 'Vietnam', 'Cambodia', 'Laos',
  'China', 'Japan', 'India', 'Sri Lanka', 'Nepal', 'Mongolia',
  'Hong Kong', 'Egypt', 'Fiji',
].map((c) => ({ value: c, label: c }));

const mapDocumentEventOption = (event) => {
  const dateLabel = event.start_date
    ? new Date(event.start_date).toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : '';
  return {
    value: String(event.id),
    label: dateLabel ? `${event.name} (${dateLabel})` : (event.name || `#${event.id}`),
  };
};

const galleryVisibilityOptions = GALLERY_PUBLICITY_FILTER_OPTIONS;

const borrowStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'returned', label: 'Returned' },
  { value: 'overdue', label: 'Overdue' },
];

const penaltyStatusOptions = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

const parcelStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'collected', label: 'Collected' },
  { value: 'returned', label: 'Returned' },
];

const parcelLocationOptions = [
  { value: 'OPC', label: 'OPC' },
  { value: 'CPP', label: 'CPP' },
  { value: 'Angkasa', label: 'Angkasa' },
  { value: 'other', label: 'Other' },
];

const requirementStatusOptions = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'in_contact', label: 'In contact' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const USER_BULK_CONFIG = {
  endpoint: '/users/bulk',
  entityLabel: 'user',
  canBulk: canManageUsers,
  canBulkDelete: canManageUsers,
  fields: (user) => {
    const fields = [
      { name: 'bureau_id', label: 'Bureau', type: 'select', loadOptions: '/bureaus', clearable: true },
      { name: 'department_id', label: 'Department', type: 'select', loadOptions: '/departments', clearable: true },
      { name: 'management_id', label: 'Management', type: 'select', loadOptions: '/managements', clearable: true },
    ];
    if (canManageUserPrivileges(user)) {
      fields.push({
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive (Gallery only)' },
        ],
      });
    }
    return fields;
  },
};

export const BUREAU_BULK_CONFIG = {
  endpoint: '/bureaus/bulk',
  entityLabel: 'bureau',
  canBulk: isAdminOrTL,
  fields: () => [{ name: 'description', label: 'Description', type: 'textarea' }],
};

export const DEPARTMENT_BULK_CONFIG = {
  endpoint: '/departments/bulk',
  entityLabel: 'department',
  canBulk: isAdminOrTL,
  fields: () => [{ name: 'description', label: 'Description', type: 'textarea' }],
};

export const MANAGEMENT_BULK_CONFIG = {
  endpoint: '/managements/bulk',
  entityLabel: 'management role',
  canBulk: isAdminOrTL,
  fields: () => [{ name: 'description', label: 'Description', type: 'textarea' }],
};

const taskStatusOptions = [
  { value: 'not_started', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'completed', label: 'Done' },
];

export const TASK_BULK_CONFIG = {
  endpoint: '/tasks/bulk',
  entityLabel: 'task',
  canBulk: isAdminOrTL,
  canBulkDelete: isAdminOrTL,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected tasks?',
  bulkDeleteDescription: 'This will archive the selected tasks. They will remain in the system for history and audit purposes.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    { name: 'status', label: 'Status', type: 'searchableSelect', options: taskStatusOptions },
    {
      name: 'department_id',
      label: 'Department',
      type: 'searchableSelect',
      loadOptions: '/departments?minimal=1',
      clearable: true,
    },
    {
      name: 'user_id',
      label: 'Assigned to',
      type: 'searchableSelect',
      loadOptions: '/users?minimal=1&active_only=1',
      clearable: true,
      filterOption: (option, values) => {
        if (!values.department_id || values.department_id === UNSET) return true;
        if (values.department_id === '') return true;
        return Number(option.raw?.department_id) === Number(values.department_id);
      },
      disabledHint: 'Choose a department first to limit assignees to that department.',
    },
  ],
  applyFieldChange: (name, value, values) => {
    const next = { ...values, [name]: value };
    if (name === 'department_id' && values.user_id && values.user_id !== UNSET) {
      next.user_id = UNSET;
    }
    return next;
  },
};

export const CLAIM_BULK_CONFIG = {
  endpoint: '/claims/bulk',
  entityLabel: 'claim',
  canBulk: canManageAllClaims,
  canBulkDelete: () => false,
  canBulkReject: canManageAllClaims,
  bulkRejectEndpoint: '/claims/bulk-reject',
  bulkRejectButtonLabel: 'Bulk reject',
  fields: () => [
    { name: 'status', label: 'Status', type: 'select', options: claimStatusOptions },
    {
      name: 'is_claimable',
      label: 'Claimable',
      type: 'select',
      options: [
        { value: '1', label: 'Yes' },
        { value: '0', label: 'No' },
      ],
    },
  ],
};

export const MAINTENANCE_BULK_CONFIG = {
  endpoint: '/maintenances/bulk',
  entityLabel: 'maintenance record',
  canBulk: canManageMaintenanceWorkflow,
  canBulkDelete: canManageMaintenanceWorkflow,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected maintenance requests?',
  bulkDeleteDescription: 'This will archive the selected maintenance requests. They will remain in the system for history and audit purposes.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    { name: 'priority', label: 'Priority', type: 'searchableSelect', options: maintenancePriorityOptions },
    { name: 'status', label: 'Status', type: 'searchableSelect', options: maintenanceStatusOptions },
    { name: 'type', label: 'Type', type: 'searchableSelect', options: maintenanceTypeOptions },
    { name: 'assigned_to_id', label: 'Assigned to', type: 'searchableSelect', loadOptions: '/users?active_only=1' },
  ],
};

export const PARCEL_BULK_CONFIG = {
  endpoint: '/parcels/bulk',
  entityLabel: 'parcel',
  canBulk: (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 5,
  canBulkDelete: (user) => isAdminOrTL(user) || Number(user?.bureau_id) === 5,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected parcels?',
  bulkDeleteDescription: 'This will archive the selected parcels. They will remain in the system for history and audit purposes.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    { name: 'status', label: 'Status', type: 'searchableSelect', options: parcelStatusOptions },
    { name: 'location', label: 'Location', type: 'searchableSelect', options: parcelLocationOptions },
    { name: 'receiver_id', label: 'Receiver', type: 'searchableSelect', loadOptions: '/users?active_only=1',
      isDisabled: (values) => values.status !== 'collected',
      isRequired: (values) => values.status === 'collected',
      showNoChange: (values) => values.status !== 'collected',
      disabledHint: 'Set status to Collected to unlock receiver.',
      requiredHint: 'Receiver is required when status is Collected.',
    },
    { name: 'received_date', label: 'Collected date', type: 'date',
      isDisabled: (values) => values.status !== 'collected',
      isRequired: (values) => values.status === 'collected',
      disabledHint: 'Set status to Collected to unlock collected date.',
      requiredHint: 'Collected date is required when status is Collected.',
    },
  ],
  applyFieldChange: (name, value, values) => {
    const next = { ...values, [name]: value };
    if (name === 'status' && value === 'collected') {
      if (next.received_date === UNSET || !next.received_date) {
        next.received_date = new Date().toISOString().slice(0, 10);
      }
    }
    if (name === 'status' && value !== 'collected') {
      next.received_date = UNSET;
      next.receiver_id = UNSET;
    }
    return next;
  },
  validatePayload: (updates, values) => {
    const errors = {};
    const hasCollectedDate = updates.received_date && updates.received_date !== UNSET;
    if (hasCollectedDate && values.status !== 'collected') {
      errors.received_date = 'Collected date can only be set when status is Collected.';
    }
    if (values.status === 'collected') {
      if (values.receiver_id === UNSET || !values.receiver_id) {
        errors.receiver_id = 'Receiver is required when status is Collected.';
      }
      if (values.received_date === UNSET || !values.received_date) {
        errors.received_date = 'Collected date is required when status is Collected.';
      }
    }
    return errors;
  },
};

export const DOCUMENT_BULK_CONFIG = {
  endpoint: '/documents/bulk',
  entityLabel: 'document',
  canBulk: isDocumentManager,
  fields: () => [
    { name: 'publicity', label: 'Visibility', type: 'searchableSelect', options: visibilityOptions },
    {
      name: 'event_id',
      label: 'Related event',
      type: 'searchableSelect',
      loadOptions: '/documents/events/list',
      clearable: true,
      mapOption: mapDocumentEventOption,
    },
  ],
};

export const GALLERY_BULK_CONFIG = {
  endpoint: '/galleries/bulk',
  entityLabel: 'gallery item',
  canBulk: canManageGallery,
  canBulkDelete: canManageGallery,
  fields: () => [
    { name: 'publicity', label: 'Publicity', type: 'searchableSelect', options: galleryVisibilityOptions },
  ],
};

export const BUDGET_BULK_CONFIG = {
  endpoint: '/budgets/bulk',
  entityLabel: 'budget item',
  canBulk: isBudgetManager,
  fields: () => [
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      min: 2020,
      max: 2050,
    },
    {
      name: 'month',
      label: 'Month (1–12)',
      type: 'number',
      min: 1,
      max: 12,
    },
  ],
};

export const PENALTY_BULK_CONFIG = {
  endpoint: '/penalties/bulk',
  entityLabel: 'penalty',
  canBulk: canCreatePenalty,
  canBulkDelete: canCreatePenalty,
  fields: () => [
    { name: 'status', label: 'Status', type: 'select', options: penaltyStatusOptions },
  ],
};

export const STOCK_BULK_CONFIG = {
  endpoint: '/stocks/bulk',
  entityLabel: 'stock item',
  canBulk: isInventoryManager,
  canBulkDelete: isInventoryManager,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected stock items?',
  bulkDeleteDescription: 'Archived stock cannot be borrowed again, but its borrow history will remain available.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    {
      name: 'status',
      label: 'Status',
      type: 'searchableSelect',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      name: 'place',
      label: 'Place / location',
      type: 'searchableSelect',
      loadOptions: '/stocks',
      dedupeByValue: true,
      mapOption: (row) => ({
        value: String(row.place || '').trim(),
        label: String(row.place || '').trim(),
      }),
    },
    {
      name: 'department_id',
      label: 'Department',
      type: 'searchableSelect',
      loadOptions: '/departments',
      clearable: true,
    },
  ],
};

export const BORROW_BULK_CONFIG = {
  endpoint: '/borrows/bulk',
  entityLabel: 'borrow record',
  canBulk: isInventoryManager,
  canBulkDelete: isInventoryManager,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected borrow records?',
  bulkDeleteDescription: 'This will archive the selected borrow records. Any selected records that have not been returned will be removed from active inventory calculations, so their stock quantities become available for new borrow records. They will remain in the system for history and audit purposes.',
  bulkDeleteConfirmLabel: 'Archive selected',
  hint: 'Returned sets the return date to today. Archived records remain available for audit history and do not reserve stock quantity.',
  fields: () => [
    {
      name: 'status',
      label: 'Status',
      type: 'searchableSelect',
      options: borrowStatusOptions,
    },
  ],
};

export const ANALYSIS_BULK_CONFIG = {
  endpoint: '/analyses/bulk',
  entityLabel: 'analysis',
  canBulk: isGameManager,
  fields: () => [
    {
      name: 'type',
      label: 'Type',
      type: 'searchableSelect',
      options: analysisTypeOptions,
    },
    {
      name: 'country',
      label: 'Country',
      type: 'searchableSelect',
      options: analysisCountryOptions,
    },
    {
      name: 'university',
      label: 'University',
      type: 'text',
    },
    {
      name: 'publicity',
      label: 'Visibility',
      type: 'searchableSelect',
      options: visibilityOptions,
    },
  ],
};

export const REQUIREMENT_BULK_CONFIG = {
  endpoint: '/requirements/bulk',
  entityLabel: 'requirement',
  canBulk: isPartnership,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected requirements?',
  bulkDeleteDescription: 'This will archive the selected requirements. Requirements will remain in the system for records.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    {
      name: 'is_emergency',
      label: 'Emergency',
      type: 'searchableSelect',
      options: [
        { value: '1', label: 'Yes (Emergency)' },
        { value: '0', label: 'No' },
      ],
    },
    { name: 'status', label: 'Status', type: 'searchableSelect', options: requirementStatusOptions },
    {
      name: 'company_id',
      label: 'Company',
      type: 'searchableSelect',
      loadOptions: '/companies/for-claim-form',
      mapOption: (company) => ({
        value: String(company.id),
        label: company.name || `#${company.id}`,
      }),
    },
    {
      name: 'responsible_id',
      label: 'Assigned to',
      type: 'searchableSelect',
      loadOptions: '/users?active_only=1',
      clearable: true,
    },
  ],
};

const companyStatusOptions = [
  { value: 'follow_up', label: 'Follow up' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const COMPANY_BULK_CONFIG = {
  endpoint: '/companies/bulk',
  entityLabel: 'company',
  canBulk: isPartnership,
  canBulkDelete: isPartnership,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected companies?',
  bulkDeleteDescription: 'This will set the selected companies status to Inactive. Companies will remain in the system for records.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    {
      name: 'representative_id',
      label: 'Representative User',
      type: 'searchableSelect',
      loadOptions: '/users?active_only=1',
      clearable: true,
    },
    { name: 'status', label: 'Status', type: 'searchableSelect', options: companyStatusOptions },
  ],
};

export const SPONSOR_TIER_BULK_CONFIG = {
  endpoint: '/sponsor-tiers/bulk',
  entityLabel: 'sponsor tier',
  canBulk: isPartnership,
  fields: () => [
    { name: 'benefit', label: 'Benefit', type: 'textarea' },
  ],
};

const eventStatusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

export const EVENT_BULK_CONFIG = {
  endpoint: '/events/bulk',
  entityLabel: 'event',
  canBulk: isAdminOrTL,
  bulkDeleteActionVerb: 'Cancel',
  bulkDeleteButtonLabel: 'Bulk cancel',
  bulkDeleteTitle: 'Cancel selected events?',
  bulkDeleteDescription: 'This will set the selected events status to Cancelled. Events will remain in the system for records.',
  bulkDeleteConfirmLabel: 'Cancel selected',
  fields: () => [
    { name: 'status', label: 'Status', type: 'select', options: eventStatusOptions },
  ],
};

export const PARTNER_BULK_CONFIG = {
  endpoint: '/partners/bulk',
  entityLabel: 'partner',
  canBulk: isPartnership,
  canBulkDelete: isPartnership,
  bulkDeleteActionVerb: 'Archive',
  bulkDeleteButtonLabel: 'Bulk archive',
  bulkDeleteTitle: 'Archive selected partners?',
  bulkDeleteDescription: 'This will archive the selected partners and hide them from public display. Partner records will remain in the system.',
  bulkDeleteConfirmLabel: 'Archive selected',
  fields: () => [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'inactive', label: 'Archived / Inactive' },
      ],
    },
    { name: 'publicity', label: 'Visibility', type: 'select', options: visibilityOptions },
  ],
};
